import {
  ConnectionType,
  type ConnectionData,
  type CreateConnectionRequest,
  type UpdateConnectionRequest,
  DatabaseType,
  LLMType,
} from '@app/shared/interfaces';
import type {
  CreateObjectStorageRequest,
  ObjectStorageConnection,
  TestObjectStorageRequest,
  UpdateObjectStorageRequest,
} from '@app/shared/interfaces/object-storage.interface';
import { ObjectStorageAuthType } from '@app/shared/interfaces/object-storage.interface';
import { normalizeConnectionDescription } from '@app/shared/utils/connection-description.utils';

/**
 * Pure form ⇄ API mapping for the add-connection dialog.
 *
 * This collapses the six near-identical `onSave*` config-building blocks and the
 * per-type `patchValue` blocks of `initializeEditMode` into a single per-type
 * descriptor table plus request builders. Everything here is pure (no Angular,
 * no HTTP), so the 6 connection types × create/edit can be exhaustively
 * unit-tested without a TestBed.
 *
 * ObjectStorage is intentionally NOT in the descriptor table: it targets a
 * different service (`ObjectStorageService`) with a different request shape and
 * no `{ type }` discriminator, so it has its own builders below.
 */

/** The connection types handled via `ConnectionService` (everything but objectstorage). */
export type StandardConnectionKey = 'database' | 'datafusion' | 'llm' | 'box' | 'service';

/** Name + description shared by every connection type. `description` is normalized by the builders. */
export interface ConnectionBasics {
  name: string;
  description: string | null | undefined;
}

/** A raw FormGroup value — untyped, so accessed via bracket notation. */
type FormValue = Record<string, unknown>;

export interface StandardConnectionDescriptor {
  /** The material tab index this type occupies. */
  tabIndex: number;
  /** The `ConnectionType` enum value sent to the server. */
  connectionType: ConnectionType;
  /** Result key emitted in `dialogRef.close({ type, connection })`. */
  resultKey: string;
  /** FormGroup raw value → the `config` payload (includes its `type` discriminator). */
  toConfig(formValue: FormValue): ConnectionData;
  /** Existing connection config → values to patch into this type's FormGroup. */
  fromConfig(config: FormValue): FormValue;
}

// ── Small coercion helpers (match the original onSave*/initializeEditMode logic) ──
function str(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value);
}
/** `''`/null/undefined → undefined; otherwise the trimmed-through string value. */
function optionalStr(value: unknown): string | undefined {
  return value ? String(value) : undefined;
}

export const STANDARD_CONNECTION_DESCRIPTORS: Record<StandardConnectionKey, StandardConnectionDescriptor> = {
  database: {
    tabIndex: 0,
    connectionType: ConnectionType.Database,
    resultKey: 'database',
    toConfig: (v) => ({
      type: ConnectionType.Database,
      databaseType: v['databaseType'] as DatabaseType,
      host: v['host'] as string,
      port: String(v['port']),
      username: v['username'] as string,
      dbname: v['dbname'] as string,
      options: optionalStr(v['options']),
      passwordSecretId: Number(v['passwordSecretId']),
    }) as unknown as ConnectionData,
    fromConfig: (config) => ({
      databaseType: str(config['databaseType'], DatabaseType.PostgreSQL),
      host: str(config['host']),
      port: str(config['port'], '5432'),
      username: str(config['username']),
      dbname: str(config['dbname']),
      passwordSecretId: config['passwordSecretId'] ?? '',
      options: str(config['options']),
    }),
  },
  datafusion: {
    tabIndex: 1,
    connectionType: ConnectionType.DataFusion,
    resultKey: 'datafusion',
    toConfig: (v) => ({
      type: ConnectionType.DataFusion,
      url: v['url'] as string,
    }) as unknown as ConnectionData,
    fromConfig: (config) => ({
      url: str(config['url']),
    }),
  },
  llm: {
    tabIndex: 2,
    connectionType: ConnectionType.LLM,
    resultKey: 'llm',
    toConfig: (v) => ({
      type: ConnectionType.LLM,
      llmType: v['llmType'] as LLMType,
      apiKeySecretId: Number(v['apiKeySecretId']),
      endpoint: optionalStr(v['endpoint']),
    }) as unknown as ConnectionData,
    fromConfig: (config) => ({
      llmType: str(config['llmType'], LLMType.OpenAI),
      apiKeySecretId: config['apiKeySecretId'] ?? '',
      endpoint: str(config['endpoint']),
    }),
  },
  box: {
    tabIndex: 3,
    connectionType: ConnectionType.Box,
    resultKey: 'box',
    // Registry model (SCRUM-614): authType is always 'ccg'; raw creds are gone.
    toConfig: (v) => {
      const ops = v['allowedOperations'] as unknown[] | undefined;
      return {
        type: ConnectionType.Box,
        authType: 'ccg',
        boxAppId: Number(v['boxAppId']),
        rootFolderId: v['rootFolderId'] as string,
        allowedOperations: ops?.length ? ops : undefined,
      } as unknown as ConnectionData;
    },
    fromConfig: (config) => ({
      boxAppId: config['boxAppId'] ?? '',
      rootFolderId: str(config['rootFolderId']),
      allowedOperations: (config['allowedOperations'] as unknown[] | null) ?? [],
    }),
  },
  service: {
    tabIndex: 5,
    connectionType: ConnectionType.Service,
    resultKey: 'service',
    toConfig: (v) => {
      const isGlobal = v['mode'] === 'global';
      const authType = v['authType'];
      let auth: Record<string, unknown> | undefined;
      if (!isGlobal && authType !== 'none') {
        auth = {
          type: authType,
          secretId: Number(v['authSecretId']),
          ...(authType === 'customHeader' ? { headerName: v['authHeaderName'] } : {}),
          ...(authType === 'queryParam' ? { paramName: v['authParamName'] } : {}),
        };
      }
      return {
        type: ConnectionType.Service,
        ...(isGlobal ? { globalServiceId: Number(v['globalServiceId']) } : {}),
        ...(!isGlobal ? { url: v['url'] } : {}),
        ...(v['timeoutMs'] ? { timeoutMs: Number(v['timeoutMs']) } : {}),
        ...(auth ? { auth } : {}),
      } as unknown as ConnectionData;
    },
    fromConfig: (config) => {
      const auth = (config['auth'] as Record<string, unknown> | undefined) ?? {};
      return {
        mode: config['globalServiceId'] != null ? 'global' : 'custom',
        globalServiceId: config['globalServiceId'] ?? '',
        url: str(config['url']),
        timeoutMs: config['timeoutMs'] ?? '',
        authType: auth['type'] ?? 'none',
        authSecretId: auth['secretId'] ?? '',
        authHeaderName: auth['headerName'] ?? '',
        authParamName: auth['paramName'] ?? '',
      };
    },
  },
};

/** Look up a standard descriptor by its tab index (replaces the onSaveConnection switch). */
export function standardDescriptorByTabIndex(tabIndex: number): StandardConnectionDescriptor | undefined {
  return Object.values(STANDARD_CONNECTION_DESCRIPTORS).find((d) => d.tabIndex === tabIndex);
}

export function buildCreateConnectionRequest(
  key: StandardConnectionKey,
  projectId: number,
  basics: ConnectionBasics,
  typeForm: FormValue,
): CreateConnectionRequest {
  const descriptor = STANDARD_CONNECTION_DESCRIPTORS[key];
  return {
    projectId,
    name: basics.name,
    description: normalizeConnectionDescription(basics.description),
    type: descriptor.connectionType,
    config: descriptor.toConfig(typeForm),
  };
}

export function buildUpdateConnectionRequest(
  key: StandardConnectionKey,
  connectionId: number,
  basics: ConnectionBasics,
  typeForm: FormValue,
): UpdateConnectionRequest {
  const descriptor = STANDARD_CONNECTION_DESCRIPTORS[key];
  return {
    connectionId,
    name: basics.name,
    description: normalizeConnectionDescription(basics.description),
    type: descriptor.connectionType,
    config: descriptor.toConfig(typeForm),
  };
}

// ── ObjectStorage (separate service + request shape) ────────────────────────

function buildObjectStoragePayload(basics: ConnectionBasics, osForm: FormValue): CreateObjectStorageRequest {
  const isOAuth = osForm['authType'] === ObjectStorageAuthType.OAuth;
  return {
    name: basics.name,
    description: normalizeConnectionDescription(basics.description),
    provider: osForm['provider'] as CreateObjectStorageRequest['provider'],
    authType: osForm['authType'] as CreateObjectStorageRequest['authType'],
    bucket: osForm['bucket'] as string,
    endpoint: optionalStr(osForm['endpoint']),
    region: optionalStr(osForm['region']),
    projectId: optionalStr(osForm['projectId']),
    accountName: optionalStr(osForm['accountName']),
    accessKey: isOAuth ? undefined : optionalStr(osForm['accessKey']),
    secretId: isOAuth ? undefined : (osForm['secretId'] ? Number(osForm['secretId']) : undefined),
    userCredentialId: isOAuth ? (osForm['userCredentialId'] ? Number(osForm['userCredentialId']) : undefined) : undefined,
  };
}

export function buildObjectStorageCreateRequest(basics: ConnectionBasics, osForm: FormValue): CreateObjectStorageRequest {
  return buildObjectStoragePayload(basics, osForm);
}

export function buildObjectStorageUpdateRequest(basics: ConnectionBasics, osForm: FormValue): UpdateObjectStorageRequest {
  return { ...buildObjectStoragePayload(basics, osForm) };
}

/**
 * Build the test-before-save request. The server reuses the create shape and
 * requires `name`, but ignores it for testing — so whitespace-only names fall
 * back to a placeholder (SCRUM-720). `userCredentialId` is never sent for a test.
 */
export function buildObjectStorageTestRequest(name: string | null | undefined, osForm: FormValue): TestObjectStorageRequest {
  const isOAuth = osForm['authType'] === ObjectStorageAuthType.OAuth;
  return {
    name: name?.trim() || 'test-connection',
    provider: osForm['provider'] as TestObjectStorageRequest['provider'],
    authType: osForm['authType'] as TestObjectStorageRequest['authType'],
    bucket: osForm['bucket'] as string,
    endpoint: optionalStr(osForm['endpoint']),
    region: optionalStr(osForm['region']),
    projectId: optionalStr(osForm['projectId']),
    accountName: optionalStr(osForm['accountName']),
    accessKey: isOAuth ? undefined : optionalStr(osForm['accessKey']),
    secretId: isOAuth ? undefined : (osForm['secretId'] ? Number(osForm['secretId']) : undefined),
  };
}

/** Existing ObjectStorage connection → objectStorageForm values (GCS id falls back across field names). */
export function objectStorageConfigToForm(existing: ObjectStorageConnection): FormValue {
  const gcsProjectId =
    (existing as unknown as { gcsProjectId?: string }).gcsProjectId ??
    (existing as unknown as { projectId?: number | string }).projectId ??
    '';
  return {
    provider: existing.provider,
    authType: existing.authType,
    bucket: existing.bucket,
    endpoint: existing.endpoint || '',
    region: existing.region || '',
    projectId: gcsProjectId,
    accountName: existing.accountName || '',
    accessKey: existing.accessKey || '',
    secretId: existing.secretId ?? '',
    userCredentialId: existing.userCredentialId ?? '',
  };
}
